import { useQuery } from "@tanstack/react-query";
import { parseISO } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";

import type { AppTicketDto } from "@/api/types";
import { Form } from "@/components/Form/Form";
import { FormStarInput } from "@/components/Form/FormStarInput";
import { FormTagSelect } from "@/components/Form/FormTagSelect";
import { FormTextArea } from "@/components/Form/FormTextArea";
import type { ModalBaseProps } from "@/components/Modal/Modal";
import { Modal } from "@/components/Modal/Modal";
import { Button } from "@/design-system/Button";
import { FormField, FormFieldControl } from "@/design-system/FormField";
import { Inline } from "@/design-system/Inline";
import { Stack } from "@/design-system/Stack";
import type { TabItem } from "@/design-system/Tabs";
import { Tabs } from "@/design-system/Tabs";
import { useBool } from "@/hooks/useBool";
import { ticketMutations } from "@/queries/tickets/mutations";
import { useTicketQueries } from "@/queries/tickets/queryOptions";
import { useTranslation } from "@/translations";

import { FormDatePicker } from "../Form/FormDatePicker";

const FORM_ID = "ticket-resident-rate-modal-form";

type TicketResidentRateModalProps = ModalBaseProps & {
  ticket: AppTicketDto;
};

const REMARK_MAX_LENGTH = 1000;

export function TicketResidentRateModal({
  ticket,
  isOpened,
  onOpenChange,
}: TicketResidentRateModalProps): React.ReactNode {
  const [maxDate] = useState(() => new Date());
  const [isCommentFieldVisible, commentFieldVisibilityHandlers] = useBool();
  const [activeTab, setActiveTab] = useState<"rating" | "noRating">("rating");

  const { t } = useTranslation();
  const form = useForm<TicketResidentRateModalFormValues>({
    defaultValues: {
      rating: 4,
      remark: "",
      feedbackOptions: [],
      closingDate: ticket.closedAt ? parseISO(ticket.closedAt) : maxDate,
    },
  });

  const currRating = useWatch({ control: form.control, name: "rating" });

  const ticketQueries = useTicketQueries();
  const { data: feedbackOptions } = useQuery(ticketQueries.ratingFeedbackOptions());
  const mutationUpdateRating = ticketMutations.useUpdateRating(ticket.id);

  useEffect(() => {
    if (isOpened) {
      commentFieldVisibilityHandlers.setFalse();
    }
  }, [isOpened, commentFieldVisibilityHandlers]);

  async function onSubmit(values: TicketResidentRateModalFormValues) {
    try {
      await mutationUpdateRating.mutateAsync({
        remark: values.remark,
        rating: activeTab === "rating" ? values.rating : undefined,
        closedAt: values.closingDate.toISOString(),
        feedbackOptions: values.feedbackOptions as any[],
      });
    } finally {
      onOpenChange(false);
    }
  }

  const onChangeActiveTab = (newActiveTab: "rating" | "noRating") => {
    if (newActiveTab === "rating") {
      commentFieldVisibilityHandlers.setFalse();
    } else {
      form.reset();
    }

    setActiveTab(newActiveTab);
  };

  const minDate = useMemo(() => {
    const safeMinDate = parseISO(ticket.postedAt);
    // Set to start of day to avoid trouble with comparison of same date
    safeMinDate.setHours(0, 0, 0, 0);

    return safeMinDate;
  }, [ticket]);

  const relevantFeedbackOptions = useMemo(
    () =>
      feedbackOptions
        ?.find((option) => {
          return option.rating === currRating;
        })
        ?.options.map((option) => ({
          label: option.value,
          value: option.label,
        })),
    [feedbackOptions, currRating],
  );
  const tabItems: TabItem<"rating" | "noRating">[] = [
    {
      value: "rating",
      label: t("page.tickets.rate-modal.tabs.with-rating"),
      "data-testid": "with-rating-tab-btn",
    },
    {
      value: "noRating",
      label: t("page.tickets.rate-modal.tabs.without-rating"),
      "data-testid": "no-rating-tab-btn",
      isHidden: ticket.status.type === "closed",
    },
  ];

  return (
    <Modal.Root
      shouldCloseOnEsc={!mutationUpdateRating.isPending}
      shouldCloseOnClickOutside={!mutationUpdateRating.isPending}
      title={t("page.tickets.rate-modal.title")}
      data-testid="ticket-rate-modal"
      description={ticket.status.type !== "closed" && t("page.tickets.rate-modal.description")}
      size="md"
      {...{ isOpened, onOpenChange }}
    >
      <Modal.Body>
        <Form.Root formMethods={form} id={FORM_ID} onSubmit={onSubmit}>
          <Tabs.Root activeTab={activeTab} onSelectTab={onChangeActiveTab} tabs={tabItems}>
            <Stack gap="md">
              <Tabs.Content value="rating">
                <Form.Body>
                  <FormField label={t("page.tickets.rate-modal.rating-field.title")}>
                    <Inline justify="center" wrap={false}>
                      <FormStarInput<TicketResidentRateModalFormValues, "rating"> name="rating" />
                    </Inline>
                  </FormField>
                  {relevantFeedbackOptions && relevantFeedbackOptions.length > 0 && (
                    <FormField
                      label={
                        currRating > 3
                          ? t("page.tickets.rate-modal.feedback-options-field.title-positive")
                          : t("page.tickets.rate-modal.feedback-options-field.title-negative")
                      }
                      description={t("page.tickets.rate-modal.rating-field.description")}
                    >
                      <FormFieldControl>
                        <FormTagSelect<TicketResidentRateModalFormValues, "feedbackOptions">
                          name="feedbackOptions"
                          items={relevantFeedbackOptions}
                          isMultiple
                          data-testid="rate-modal-feedback-option-field"
                        />
                      </FormFieldControl>
                    </FormField>
                  )}
                  {isCommentFieldVisible ? (
                    <FormField label={t("page.tickets.rate-modal.remark-field.title")}>
                      <FormTextArea<TicketResidentRateModalFormValues>
                        name="remark"
                        placeholder={t("page.tickets.rate-modal.remark-field.placeholder")}
                        rules={{
                          maxLength: {
                            message: t("components.form.error.max-length", {
                              length: REMARK_MAX_LENGTH,
                            }),
                            value: REMARK_MAX_LENGTH,
                          },
                        }}
                      />
                    </FormField>
                  ) : (
                    <Inline justify="center" wrap={false}>
                      <Button
                        onClick={commentFieldVisibilityHandlers.setTrue}
                        variant="ghostPrimary"
                        type="button"
                        data-testid="rate-modal-toggle-comment-input-btn"
                      >
                        {t("page.tickets.rate-modal.leave-a-comment.btn")}
                      </Button>
                    </Inline>
                  )}
                </Form.Body>
              </Tabs.Content>
              <Tabs.Content value="noRating">
                <FormField label={t("page.tickets.rate-modal.remark-field.title")}>
                  <FormTextArea<TicketResidentRateModalFormValues>
                    name="remark"
                    placeholder={t("page.tickets.rate-modal.remark-field.placeholder")}
                    rules={{
                      maxLength: {
                        message: t("components.form.error.max-length", {
                          length: REMARK_MAX_LENGTH,
                        }),
                        value: REMARK_MAX_LENGTH,
                      },
                    }}
                  />
                </FormField>
              </Tabs.Content>
              {ticket.status.type === "closed" && (
                <FormField label={t("page.tickets.rate-modal.closing-date")}>
                  <FormDatePicker<TicketResidentRateModalFormValues>
                    name="closingDate"
                    min={minDate}
                    max={maxDate}
                    rules={{
                      validate: {
                        laterThanMin: (date) => {
                          if (!date) {
                            return undefined;
                          }

                          return date < minDate
                            ? t("page.tickets.rate-modal.closing-date.error.must-be-after-open")
                            : undefined;
                        },
                        soonerThanMax: (date) => {
                          if (!date) {
                            return undefined;
                          }

                          return date > maxDate
                            ? t("page.tickets.rate-modal.closing-date.error.must-be-in-past")
                            : undefined;
                        },
                      },
                    }}
                  />
                </FormField>
              )}
            </Stack>
          </Tabs.Root>
        </Form.Root>
      </Modal.Body>
      <Modal.Actions>
        <Button
          type="button"
          variant="outline"
          tone="primary"
          onClick={() => onOpenChange(false)}
          disabled={mutationUpdateRating.isPending}
          data-testid="rate-modal-cancel"
        >
          {t("common.action.cancel")}
        </Button>
        <Button
          form={FORM_ID}
          type="submit"
          variant="filled"
          isLoading={mutationUpdateRating.isPending}
          data-testid="rate-modal-submit"
        >
          {t("page.tickets.rate-modal.submit-btn")}
        </Button>
      </Modal.Actions>
    </Modal.Root>
  );
}

interface TicketResidentRateModalFormValues {
  rating: number;
  remark: string;
  feedbackOptions: string[];
  closingDate: Date;
}
